import { createAsyncThunk } from '@reduxjs/toolkit';

import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import { captureMessage } from '@sentry/nextjs';

import type { ApiResponseModel } from '@nocowanie/gql-core/models';

import { PropertyTypeApiMapping, SliceNameEnum } from '@app/enums';
import * as accommodationHelpers from '@app/helpers/accommodation.helpers';
import { getAccommodationLinkspaces } from '@app/helpers/linkspace.helpers';
import { AccommodationStaticStateModel, apiTypes, RequestAdditionalParamsModel } from '@app/models';
import { BestFor } from '@app/models/api/api-types';
import {
    AccommodationPageModel,
    BreadcrumbsModel,
    LinkspaceModel,
    PointOfInterestModel,
} from '@app/models/elastic-search';
import { RatingModel } from '@app/models/elastic-search/rating.model';
import { CmsAccommodationService } from '@app/services/cms-accommodation.service';
import { EsAccommodationBestsellerService } from '@app/services/es-accommodation-bestseller.service';
import { EsAccommodationPageService } from '@app/services/es-accommodation-page.service';
import { EsAccommodationRatingService } from '@app/services/es-accommodation-rating.service';
import { EsGeoService } from '@app/services/es-geo.service';
import { EsLinkspacesService } from '@app/services/es-linkspace.service';

export interface AccommodationStaticResponse {
    staticData?: Omit<AccommodationPageModel, 'breadcrumbs'>;
    ratings?: {
        collection?: Array<RatingModel>;
    };
    linkSpace?: Array<LinkspaceModel>;
    breadcrumbs?: BreadcrumbsModel;
    pointOfInterest?: Array<PointOfInterestModel>;
    isBestseller?: boolean;
    cityDetails?: AccommodationStaticStateModel['cityDetails'];
    cmsDescription?: string | null;
}

const getInternalLabels = (
    args: apiTypes.QueryAccommodationsArgs,
    internalLabels: apiTypes.InternalLabel,
): apiTypes.InternalLabel => {
    const { childrenCount, guestsCount } = args;

    const children = Number(childrenCount ?? 0);
    const adults = Number(guestsCount ?? 0);

    if (children === 0 && adults === 2) {
        internalLabels.bestFor = [...(internalLabels.bestFor ?? []), BestFor.Couple];
    }

    if (adults > 2) {
        internalLabels.bestFor = [...(internalLabels.bestFor ?? []), BestFor.Friends];
    }

    if (children > 0 && [1, 2].includes(adults)) {
        internalLabels.bestFor = [...(internalLabels.bestFor ?? []), BestFor.Family];
    }

    let key: keyof typeof internalLabels;

    // BACKWARD COMPATIBILITY
    for (key in internalLabels) {
        if (!isArray(internalLabels[key]) || isNil(internalLabels[key])) {
            continue;
        }

        (internalLabels[key] as any) = (internalLabels[key] as any).map(
            (x: { name?: string }) => x?.name ?? x,
        );
    }

    return internalLabels;
};

export const getAccommodationStatic = createAsyncThunk<
    ApiResponseModel<AccommodationStaticResponse>,
    apiTypes.QueryAccommodationArgs & RequestAdditionalParamsModel
>(
    `${SliceNameEnum.AccommodationStatic}/fetch-data`,
    async (args, { rejectWithValue }): Promise<any> => {
        try {
            const accommodationId = args.id.split('/').pop() ?? '';

            const [
                accommodationStaticResult,
                ratingsResult,
                bestsellerResult,
                cmsDescriptionResult,
            ] = await Promise.allSettled([
                EsAccommodationPageService.instance.loadAccommodationData(accommodationId),
                EsAccommodationRatingService.instance.loadAccommodationRatings(
                    accommodationId,
                    0,
                    10,
                ),
                EsAccommodationBestsellerService.instance.loadBestseller(accommodationId),
                CmsAccommodationService.instance.getDescription(accommodationId),
            ]);

            if (
                accommodationStaticResult.status !== 'fulfilled' ||
                !accommodationStaticResult.value?.found
            ) {
                return {
                    data: {
                        staticData: null,
                    },
                };
            }

            const {
                breadcrumbs,
                features,
                stay: { checkInStart, checkInEnd, checkOutStart, checkOutEnd, ...stay },
                ...staticData
            } = accommodationStaticResult.value._source;

            const cityDetails = await EsGeoService.instance.getCityDetails(
                staticData.location.settlement.id,
            );

            const linkspaceDetails = await EsLinkspacesService.instance.loadAccommodationLinkspaces(
                accommodationId,
                `${cityDetails?.id}`,
                `${PropertyTypeApiMapping[staticData.type as keyof typeof PropertyTypeApiMapping]}`,
            );

            if (isNil(breadcrumbs?.items)) {
                captureMessage(`Missing breadcrumbs for NOP_ID ${accommodationId}`, 'error');
            }

            const ratings =
                ratingsResult.status === 'fulfilled'
                    ? ratingsResult.value.hits.map(({ _source }) => ({
                          ..._source,
                          average: Number(_source.average ?? 0),
                      }))
                    : undefined;

            const isBestseller =
                bestsellerResult.status === 'fulfilled' ? bestsellerResult.value.found : undefined;

            const linkSpace = getAccommodationLinkspaces(linkspaceDetails, breadcrumbs);

            const cmsDescription =
                cmsDescriptionResult.status === 'fulfilled' ? cmsDescriptionResult.value : null;

            const result: AccommodationStaticResponse = {
                staticData: {
                    ...staticData,
                    features: {
                        ...features,
                        internalLabel: getInternalLabels(args, features.internalLabel),
                    },
                    stay: {
                        ...stay,
                        checkInStart: !isNil(checkInStart)
                            ? accommodationHelpers.formatTimeFromString(checkInStart)
                            : checkInStart,
                        checkInEnd: !isNil(checkInEnd)
                            ? accommodationHelpers.formatTimeFromString(checkInEnd)
                            : checkInEnd,
                        checkOutStart: !isNil(checkOutStart)
                            ? accommodationHelpers.formatTimeFromString(checkOutStart)
                            : checkOutStart,
                        checkOutEnd: !isNil(checkOutEnd)
                            ? accommodationHelpers.formatTimeFromString(checkOutEnd)
                            : checkOutEnd,
                    },
                },
                ratings: !isNil(ratings) ? { collection: ratings } : undefined,
                breadcrumbs,
                linkSpace,
                isBestseller,
                cityDetails,
                cmsDescription,
            };

            // TODO: temporary commented (https://szallashugroup.atlassian.net/browse/DNI-1637)
            // if (pointOfInterest.status === 'fulfilled' && !isNil(pointOfInterest.value)) {
            //     // Open api type definition doesn't match what we get, so we have to cast.
            //     result.pointOfInterest = pointOfInterest.value as PointOfInterest[];
            // } else if (pointOfInterest.status === 'rejected') {
            //     //TODO: handle if pointOfInterest crash
            //     console.error('err', pointOfInterest.reason);
            // }
            return { data: result };
        } catch (err) {
            return rejectWithValue([err].flat());
        }
    },
);
